'use client';

import { useEffect, useRef, useState } from 'react';

import type { TenantVerifiedPurchase } from '~/(tenant)/_lib/analytics/tenant-purchase.types';
import {
  type TenantReturnPurchaseRequest,
  getTenantReturnPurchaseAction,
} from '~/(tenant)/_lib/analytics/tenant-return-purchase.actions';

import { useOptionalTenantAnalytics } from './tenant-analytics-provider';
import { isTenantAnalyticsDeliveryComplete } from './tenant-delivery-status';
import {
  type TenantPurchaseSafeDocumentPath,
  maskTenantPurchaseDocumentLocation,
} from './tenant-purchase-document-location';

const DEDUP_STORAGE_PREFIX = 'tenant-analytics-purchase';
const RETURN_PURCHASE_ATTEMPTS = 4;
const RETURN_PURCHASE_RETRY_DELAY_MS = 500;
/**
 * How long to wait for every consented SDK to load before delivering anyway.
 * While the vendor scripts are still loading their command queues would read
 * the document URL later, after the mask below has been lifted, so delivery
 * waits for readiness; a blocked pixel script must not hold Google delivery
 * hostage forever, hence the cap.
 */
const DELIVERY_READY_TIMEOUT_MS = 5_000;

/**
 * Fires one canonical purchase event for a server-verified purchase payload.
 * Delivery is deduplicated twice: a sessionStorage marker stops same-session
 * replays (refresh, back navigation), and the deterministic transactionId lets
 * providers collapse anything that still gets through.
 */
export function TenantPurchaseTracker({
  purchase,
  safeDocumentPath,
}: {
  readonly purchase: TenantVerifiedPurchase | null;
  /**
   * When set, the payment-return URL is masked with this static path for the
   * instant the social pixels read it, then restored. Required on any
   * confirmation route with a dynamic segment or provider query parameters;
   * without it the pixel adapters reject the Purchase.
   */
  readonly safeDocumentPath?: TenantPurchaseSafeDocumentPath;
}) {
  const analytics = useOptionalTenantAnalytics();
  const attemptedKeyRef = useRef<string | null>(null);
  const [readinessWaived, setReadinessWaived] = useState(false);
  const deliveryReady = analytics?.deliveryReady ?? false;
  const enabled = analytics?.enabled ?? false;
  const transportKey = analytics?.transportKey ?? null;
  const trackPurchase = analytics?.trackPurchase;

  useEffect(() => {
    if (!purchase || !enabled || deliveryReady || readinessWaived) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => setReadinessWaived(true),
      DELIVERY_READY_TIMEOUT_MS,
    );

    return () => window.clearTimeout(timeoutId);
  }, [deliveryReady, enabled, purchase, readinessWaived]);

  useEffect(() => {
    if (!purchase || !enabled || !transportKey || !trackPurchase) {
      return;
    }

    if (!deliveryReady && !readinessWaived) {
      return;
    }

    const dedupKey = [
      DEDUP_STORAGE_PREFIX,
      transportKey,
      purchase.kind,
      purchase.transactionId,
    ].join(':');

    // Ref guard: Strict Mode double-invocation and consent-driven re-renders
    // must not fire twice before the storage marker lands.
    if (attemptedKeyRef.current === dedupKey || readMarker(dedupKey)) {
      return;
    }

    attemptedKeyRef.current = dedupKey;

    // Only mask once the SDKs are loaded: a queued command would read the URL
    // after the mask is lifted. If readiness was waived, the pixels reject the
    // unsafe URL themselves and Google delivery proceeds.
    const restoreDocumentLocation =
      safeDocumentPath && deliveryReady
        ? maskTenantPurchaseDocumentLocation(safeDocumentPath)
        : null;

    void trackPurchase(purchase)
      .then(
        (results) => {
          if (deliveryReady && isTenantAnalyticsDeliveryComplete(results)) {
            writeMarker(dedupKey);
            return;
          }

          // A genuine delivery failure remains retryable after provider
          // recovery or a later visit. Consent-denied adapters are terminal
          // for this session marker so delivered transports do not fire again.
          attemptedKeyRef.current = null;
        },
        () => {
          attemptedKeyRef.current = null;
        },
      )
      .finally(() => {
        restoreDocumentLocation?.();
      });
  }, [
    deliveryReady,
    enabled,
    purchase,
    readinessWaived,
    safeDocumentPath,
    trackPurchase,
    transportKey,
  ]);

  return null;
}

/**
 * Payment-return variant: fetches the purchase with the payment's own
 * reference, retrying briefly while a webhook may still be writing the ledger,
 * then defers to TenantPurchaseTracker. Renders nothing and never throws —
 * analytics must not affect a confirmation page.
 */
export function TenantReturnPurchaseTracker({
  request,
  safeDocumentPath,
}: {
  readonly request: TenantReturnPurchaseRequest;
  readonly safeDocumentPath?: TenantPurchaseSafeDocumentPath;
}) {
  const analytics = useOptionalTenantAnalytics();
  const enabled = analytics?.enabled ?? false;
  const transportKey = analytics?.transportKey ?? null;
  const [purchase, setPurchase] = useState<TenantVerifiedPurchase | null>(null);
  const requestedKeyRef = useRef<string | null>(null);
  // Callers build the request inline; key the effect on its content.
  const requestKey = JSON.stringify(request);

  useEffect(() => {
    if (!enabled || !transportKey || requestedKeyRef.current === requestKey) {
      return;
    }

    // Set before the async work so a Strict Mode re-run or a remount with the
    // same request does not start a second lookup.
    requestedKeyRef.current = requestKey;

    const returnPurchaseRequest = JSON.parse(
      requestKey,
    ) as TenantReturnPurchaseRequest;

    // Skip the lookup entirely when this purchase was already delivered in
    // this browser session (the transaction id is the request reference).
    const deliveredKey = [
      DEDUP_STORAGE_PREFIX,
      transportKey,
      returnPurchaseRequest.kind,
      returnPurchaseRequest.kind === 'package_purchase'
        ? returnPurchaseRequest.paymentReference
        : returnPurchaseRequest.bookingId,
    ].join(':');

    if (readMarker(deliveredKey)) {
      return;
    }

    void loadReturnPurchase(returnPurchaseRequest).then((loadedPurchase) => {
      if (requestedKeyRef.current !== requestKey) {
        return;
      }

      if (loadedPurchase) {
        setPurchase(loadedPurchase);
      } else {
        // A later consent/transport transition may retry.
        requestedKeyRef.current = null;
      }
    });
  }, [enabled, requestKey, transportKey]);

  return (
    <TenantPurchaseTracker
      purchase={purchase}
      safeDocumentPath={safeDocumentPath}
    />
  );
}

async function loadReturnPurchase(request: TenantReturnPurchaseRequest) {
  for (let attempt = 1; attempt <= RETURN_PURCHASE_ATTEMPTS; attempt += 1) {
    try {
      const { purchase } = await getTenantReturnPurchaseAction(request);

      if (purchase) {
        return purchase;
      }
    } catch {
      // Retry below; a failed lookup only means no conversion is reported.
    }

    if (attempt < RETURN_PURCHASE_ATTEMPTS) {
      await wait(RETURN_PURCHASE_RETRY_DELAY_MS * attempt);
    }
  }

  return null;
}

function readMarker(key: string) {
  try {
    return window.sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function writeMarker(key: string) {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Storage can be unavailable in privacy modes; provider-side
    // transaction_id deduplication remains in effect.
  }
}

function wait(milliseconds: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });
}
